import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrashIcon } from '@heroicons/react/24/outline';
import type { ScriptShot, ScriptScene, ScriptCharacter, ScriptProp } from '@packages/global';
import ShotCardContent from './ShotCardContent';
import ShotCardThumbnails from './ShotCardThumbnails';
import { findThumbnails } from '../utils/thumbnailUtils';
import ConfirmDialog from '../../common/ConfirmDialog';
import { episodeScriptApi } from '../../../services/user/episodeScript.api';

interface ShotCardProps {
  shot: ScriptShot;
  index: number;
  scriptId?: string;
  scenes?: ScriptScene[];
  characters?: ScriptCharacter[];
  props?: ScriptProp[];
  onUpdate?: () => void;
  isNew?: boolean;
  allShots?: ScriptShot[];
}

/**
 * 分镜卡片组件
 * 
 * 组合卡片内容和缩略图区域，提供完整的分镜卡片展示
 */
export default function ShotCard({
  shot,
  index,
  scriptId,
  scenes = [],
  characters = [],
  props = [],
  onUpdate,
  isNew = false,
  allShots = []
}: ShotCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { t } = useTranslation(['script', 'common']);

  // 查找缩略图数据
  const thumbnails = findThumbnails(shot, scenes, characters, props);

  const handleDeleteShot = async () => {
    if (!scriptId) return;
    setDeleting(true);
    try {
      await episodeScriptApi.deleteShot(scriptId, index);
      onUpdate?.();
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete shot:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div
        className={`
          bg-bg-surface rounded-lg p-4 group
          hover:shadow-lg hover:shadow-black/20
          transition-all duration-300 ease-out
          ${isNew ? 'opacity-0 translate-y-4 animate-fade-in-up' : 'opacity-100 translate-y-0'}
        `}
      >
        {/* 标题行：分镜编号、时长、删除按钮 */}
        <div className="flex items-center justify-between mb-3 border-b border-border-subtle pb-2">
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-gray-200">
              {t('shots.shotLabel', { number: String(index + 1).padStart(2, '0') })}
            </span>
            {shot.duration !== undefined && shot.duration > 0 && (
              <span className="text-xs text-gray-400 bg-bg-elevated px-2 py-0.5 rounded">
                {shot.duration}s
              </span>
            )}
          </div>
          {scriptId && (
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="p-1 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
              title={t('shots.deleteShot')}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* 内容行：左侧缩略图 + 右侧内容 */}
        <div className="flex gap-4">
          {/* 左侧缩略图区域 */}
          <ShotCardThumbnails thumbnails={thumbnails} />

          {/* 右侧主内容区域 */}
          <div className="flex-1 min-w-0">
            <ShotCardContent
              shot={shot}
              index={index}
              scriptId={scriptId}
              scenes={scenes}
              characters={characters}
              props={props}
              onUpdate={onUpdate}
              allShots={allShots}
            />
          </div>
        </div>
      </div>

      {/* 删除确认对话框 */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteShot}
        title={t('shots.deleteShot')}
        message={t('shots.deleteConfirm', { number: String(index + 1).padStart(2, '0') })}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
      />
    </>
  );
}
